import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { errMsg, fmt } from '../../utils/format';
import api from '../../utils/api';
import FeedModal from './FeedModal';

const formatLabel = { csv: 'CSV', json: 'JSON', xlsx: 'XLSX' };

export default function ThirdPartyFeeds({ isAdmin }) {
  const { data: feeds, loading, refetch } = useFetch('/api/integrations/feeds');
  const [editFeed, setEditFeed] = useState(null); // null | 'new' | feed obj
  const [syncing, setSyncing] = useState(null);   // feedId being synced

  const sync = async (feed) => {
    setSyncing(feed.id);
    try {
      await api.post(`/api/integrations/feeds/${feed.id}/sync`);
      setTimeout(refetch, 2000);
    } catch (err) { alert(errMsg(err)); }
    finally { setSyncing(null); }
  };

  const deleteFeed = async (feed) => {
    if (!window.confirm(`Delete "${feed.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/api/integrations/feeds/${feed.id}`); refetch(); }
    catch (err) { alert(errMsg(err)); }
  };

  const toggleEnabled = async (feed) => {
    try {
      await api.put(`/api/integrations/feeds/${feed.id}`, {
        ...feed, enabled: !feed.enabled,
        apiKey: feed.api_key, authMethod: feed.auth_method,
        authParamName: feed.auth_param_name, authHeaderName: feed.auth_header_name,
        syncTime: feed.sync_time, jsonPath: feed.json_path,
        matchKeyFeedField: feed.match_key_feed_field, matchKeySfField: feed.match_key_sf_field,
        fieldMappings: feed.field_mappings,
      });
      refetch();
    } catch (err) { alert(errMsg(err)); }
  };

  return (
    <>
      {editFeed && (
        <FeedModal
          feed={editFeed === 'new' ? null : editFeed}
          onClose={() => setEditFeed(null)}
          onSaved={refetch}
        />
      )}

      <div style={{ display:'flex', alignItems:'center', marginBottom:8, marginTop:8 }}>
        <div style={{ fontWeight:500, fontSize:13, color:'var(--text-secondary)' }}>
          <i className="ti ti-plug" style={{ marginRight:6 }} />
          Third Party Feeds
        </div>
        <div style={{ flex:1 }} />
        {isAdmin && (
          <button className="btn btn-sm btn-primary" onClick={() => setEditFeed('new')}>
            <i className="ti ti-plus" /> Add feed
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'8px 0' }}>Loading feeds…</div>}

      {(feeds || []).map(feed => (
        <div key={feed.id} className="card" style={{ marginBottom:8, opacity: feed.enabled ? 1 : 0.6 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <i className="ti ti-plug" style={{ fontSize:22, color:'var(--color-primary)' }} />
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:500, display:'flex', alignItems:'center', gap:8 }}>
                {feed.name}
                <span className="badge badge-gray" style={{ fontSize:10 }}>{formatLabel[feed.format] || feed.format}</span>
                {!feed.enabled && <span className="badge" style={{ fontSize:10, background:'var(--bg-secondary)', color:'var(--text-tertiary)' }}>Disabled</span>}
              </div>
              <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>
                {feed.last_sync
                  ? `Last sync: ${fmt.dateTime(feed.last_sync)}`
                  : `No syncs yet — scheduled at ${feed.sync_time}`}
                <span style={{ marginLeft:12 }}>
                  {(feed.field_mappings || []).filter(m => m.enabled).length} active mappings
                </span>
              </div>
            </div>
            {isAdmin && (
              <div style={{ display:'flex', gap:6 }}>
                <button className="btn btn-sm" onClick={() => toggleEnabled(feed)} title={feed.enabled ? 'Disable' : 'Enable'}>
                  <i className={`ti ${feed.enabled ? 'ti-player-pause' : 'ti-player-play'}`} />
                </button>
                <button className="btn btn-sm" onClick={() => setEditFeed(feed)}>
                  <i className="ti ti-pencil" /> Edit
                </button>
                <button className="btn btn-sm btn-primary" onClick={() => sync(feed)} disabled={syncing === feed.id}>
                  <i className="ti ti-refresh" /> {syncing === feed.id ? 'Starting…' : 'Sync now'}
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => deleteFeed(feed)}>
                  <i className="ti ti-trash" />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {!loading && !(feeds || []).length && (
        <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'12px 0' }}>
          No feeds configured. Click "Add feed" to get started.
        </div>
      )}
    </>
  );
}
